import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PhoneIncoming, Users, Voicemail } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { api } from '@/api/api-client';
import { usePermissions } from '@/hooks/usePermissions';
import { SettingsCard, SettingsGroup } from './SettingsPrimitives';

/**
 * Paramètres > Appels entrants (W4, B2-settings-numbers).
 *
 * What happens when a prospect calls one of the organisation's numbers. It
 * was written nowhere on screen (Minari has its own « Incoming calls » page).
 * The rules are the server's, and this page states them as they are:
 *
 *   - every caller and tenant admin whose XDial was seen in the last minute
 *     rings at once (inboundTargetIdentities, _inboundRouting.ts, shared by
 *     Twilio and Asterisk);
 *   - no answer within 20 s (<Dial timeout="20">), or nobody connected: the
 *     caller leaves a message, recorded and transcribed, and the call shows
 *     in Historique (twilio-twiml.routes.ts, voicemail-fallback).
 *
 * Nothing here is configurable yet (no forwarding, no ring group): changing
 * the routing means changing the TwiML, which is tested in staging first
 * (CLAUDE.md). If these rules change, this page changes with them.
 *
 * The numbers that receive calls are the organisation's active lines; a
 * personal number verified for caller ID only presents the number and
 * receives nothing.
 */

interface TenantNumber {
  id: string;
  phone_number: string;
  provider?: string;
  status?: string;
}

const HISTORY_INBOUND_PATH = '/ParallelDialer/call-history?direction=inbound';
const NUMBERS_PATH = '/ParallelDialer/settings?section=numbers';

function Rule({ icon: Icon, title, children, last }: { icon: ComponentType<{ className?: string }>; title: string; children: ReactNode; last?: boolean }) {
  return (
    <div className={`px-5 py-4 flex gap-3 ${last ? '' : 'border-b border-border'}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {children}
      </div>
    </div>
  );
}

export function IncomingCallsSection({ d }: { d: { callerData?: unknown } }) {
  const { t } = useTranslation(['settings', 'common']);
  const navigate = useNavigate();
  const { isTenantAdmin } = usePermissions();

  // Same query and key as Numéros: the cache is shared.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['tenant-numbers'],
    queryFn: async () => {
      const { data } = await api.functions.invoke('listTenantNumbers');
      return data as { numbers?: TenantNumber[] };
    },
    enabled: !!d.callerData,
  });
  const receiving = (data?.numbers || []).filter((n) => n.provider !== 'personal' && n.status === 'active');

  return (
    <div className="space-y-8">
      <SettingsGroup title={t('settings:incoming.rulesTitle')}>
        <SettingsCard>
          <Rule icon={Users} title={t('settings:incoming.whoRingsTitle')}>
            <p className="text-sm text-muted-foreground">{t('settings:incoming.whoRings')}</p>
          </Rule>
          <Rule icon={Voicemail} title={t('settings:incoming.noAnswerTitle')} last>
            <p className="text-sm text-muted-foreground">{t('settings:incoming.noAnswer')}</p>
            <Button variant="outline" size="sm" className="h-9 mt-2" onClick={() => navigate(HISTORY_INBOUND_PATH)}>
              {t('settings:incoming.seeInbound')}
            </Button>
          </Rule>
        </SettingsCard>
      </SettingsGroup>

      <SettingsGroup title={t('settings:incoming.numbersTitle')}>
        <SettingsCard>
          {isLoading ? (
            <p className="px-5 py-4 text-sm text-muted-foreground">{t('common:actions.loading')}</p>
          ) : isError ? (
            <p className="px-5 py-4 text-sm text-destructive" role="alert">{t('settings:incoming.numbersError')}</p>
          ) : receiving.length === 0 ? (
            <div className="px-5 py-6 text-center space-y-3" data-testid="incoming-empty">
              <p className="text-sm text-muted-foreground">
                {isTenantAdmin ? t('settings:incoming.noNumber') : t('settings:numbers.askAdmin')}
              </p>
              {isTenantAdmin && (
                <Button size="sm" className="h-9" onClick={() => navigate(NUMBERS_PATH)}>
                  {t('settings:numbers.getNumber')}
                </Button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-border" data-testid="incoming-numbers">
              {receiving.map((n) => (
                <li key={n.id} className="px-5 py-3 flex items-center gap-3">
                  <PhoneIncoming className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
                  <span className="text-sm text-foreground vf-font-mono">
                    {/x/i.test(n.phone_number || '') ? t('settings:numbers.numberPending') : n.phone_number}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </SettingsCard>
      </SettingsGroup>
    </div>
  );
}
